import {BillingCycle, CustomerCreditBalance, PlanKind, PlanType, ResolvedAffiliate} from "@/app/lib/pricing/types";
import {useCallback, useEffect, useState} from "react";
import {User} from "@supabase/supabase-js";
import {useMantineColorScheme} from "@mantine/core";
import {useProfile} from "@/components/common/hooks/useProfile";
import {UserProfile} from "@/app/lib/profile/user-profile";
import {useOnlineStatus} from "@/components/common/hooks/useOnlineStatus";
import * as Sentry from "@sentry/nextjs";

export type PricingDetails = {
    isOnline: boolean,
    loadingUser: boolean;
    user: User | undefined;
    profile: UserProfile | undefined;
    loadingProfile: boolean;
    loggedIn: boolean;
    subscriptionError: boolean;
    wantedPlan: PlanType | null;
    wantedKind: PlanKind | null;
    wantedBilling: BillingCycle | null;
    existingPlan: PlanType | null;
    existingKind: PlanKind | null;
    existingBilling: BillingCycle | null;
    isCancelled: boolean;
    endsAt: string | null;
    renewsAt: string | null;
    paymentMethod: string | null;
    feedbackDiscount: number;
    affiliateDiscount: number;
    // api
    apiCallInProgress: boolean;
    createDiscount: (affiliate: ResolvedAffiliate | undefined) => Promise<{ discountId: string | null; } | Error>;
    removeDiscount: (discountId: string) => Promise<void>;
    getCreditBalance: () => Promise<CustomerCreditBalance[]>;
};

export function usePricing(affiliate: ResolvedAffiliate | undefined = undefined): PricingDetails {
    const {colorScheme: scheme} = useMantineColorScheme();
    const darkMode = scheme === 'dark';

    const isOnline = useOnlineStatus();

    const {
        loadingUser,
        loadingProfile,
        user,
        profile,
    } = useProfile();

    const [apiCallInProgress, setApiCallInProgress] = useState(false);

    const sub = profile?.subscription;
    const subscriptionError = profile?.subscriptionError ?? false;
    const isCancelled = sub?.isCancelled ?? false;

    const existingPlan = isCancelled ? null : sub?.plan ?? null;
    const existingKind = isCancelled ? null : sub?.kind ?? null;
    const existingBilling = isCancelled ? null : sub?.billing ?? null;
    const endsAt = sub?.endsAt ?? null;
    const renewsAt = sub?.renewsAt ?? null;
    const paymentMethod = sub?.paymentMethod ?? null;

    const wantedPlan: PlanType | null = existingPlan ? null : user?.user_metadata?.wantedPlan as PlanType | undefined ?? null;
    const wantedKind: PlanKind | null = existingPlan ? null : user?.user_metadata?.wantedKind as PlanKind | undefined ?? null;
    const wantedBilling: BillingCycle | null = existingPlan ? null : user?.user_metadata?.wantedBilling as BillingCycle | undefined ?? null;

    const removeDiscount = useCallback(async (discountId: string): Promise<void> => {
        setApiCallInProgress(true);
        try {
            await fetch('/api/discounts/delete', {
                method: 'POST',
                body: JSON.stringify({
                    discountId
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
        } catch (error) {
            Sentry.captureException(error);
            // ignore
        } finally {
            setApiCallInProgress(false);
        }
    }, []);

    const createDiscount = useCallback(async (affiliate: ResolvedAffiliate | undefined): Promise<{ discountId: string | null } | Error> => {
        setApiCallInProgress(true);
        try {
            const response = await fetch('/api/discounts/create', {
                method: 'POST',
                body: JSON.stringify({
                    affiliate
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const data = await response.json() as { discountId: string | null }
            // console.log('checkout data', data);
            return {
                discountId: data.discountId,
            };
        } catch (error) {
            Sentry.captureException(error);
            console.error('Error creating a discount', error);
            const result = error instanceof Error ? error : new Error(String(error));
            return result;
        } finally {
            setApiCallInProgress(false);
        }
    }, []);

    const [fetchedAffiliateDiscount, setFetchedAffiliateDiscount] = useState(false);
    const [affiliateDiscount, setAffiliateDiscount] = useState(0);

    useEffect(() => {
        if (!loadingProfile && profile == null && !fetchedAffiliateDiscount) {
            // console.log('loadingProfile', loadingProfile, 'profile', profile, 'fetchedAffiliateDiscount', fetchedAffiliateDiscount);
            setApiCallInProgress(true);
            fetch('/api/affiliate-discount', {
                method: 'POST',
                body: JSON.stringify({
                    affiliate
                }),
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => {
                    if (!response.ok) {
                        throw new Error(response.statusText);
                    }
                    return response.json();
                })
                .then(data => {
                    const discountData = data as { discount: number };
                    setAffiliateDiscount(discountData.discount);
                })
                .catch(error => {
                    Sentry.captureException(error);
                    console.error('Failed to fetch affiliate discount', error);
                })
                .finally(() => {
                    setFetchedAffiliateDiscount(true);
                    setApiCallInProgress(false);
                });
        }
    }, [affiliate, fetchedAffiliateDiscount, loadingProfile, profile]);

    const getCreditBalance = useCallback(async (): Promise<CustomerCreditBalance[]> => {
        setApiCallInProgress(true);
        try {
            const response = await fetch('/api/subscription/credit-balance', {
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (!response.ok) {
                throw new Error(response.statusText);
            }
            const body = await response.json() as { creditBalances: CustomerCreditBalance[] };
            return body.creditBalances;
        } catch (error) {
            Sentry.captureException(error);
            console.error('Failed to retrieve customer credit balance', error);
            throw error instanceof Error ? error : new Error(String(error));
        } finally {
            setApiCallInProgress(false);
        }
    }, []);

    return {
        isOnline,
        loadingUser,
        user,
        loadingProfile: loadingProfile || (profile == null && !fetchedAffiliateDiscount),
        loggedIn: user != null,
        subscriptionError,
        wantedPlan,
        wantedKind,
        wantedBilling,
        existingPlan,
        existingKind,
        existingBilling,
        isCancelled,
        endsAt,
        renewsAt,
        paymentMethod,
        feedbackDiscount: profile?.feedbackDiscount ?? 0,
        affiliateDiscount: profile != null ? profile.affiliateDiscount : affiliateDiscount,
        profile,
        // api
        apiCallInProgress,
        createDiscount,
        removeDiscount,
        getCreditBalance
    };
}
